import React, { FC, memo, ReactNode } from 'react';
import styled from 'styled-components';
import { MeasurementEntry } from '@higo/common/src/features/ExaminationPage/sections/VitalSignsSection/common/MeasurementEntry';
import { MeasurementSkeleton } from '@higo/common/src/features/ExaminationPage/sections/VitalSignsSection/common/MeasurementSkeleton';
import { useBloodPressurePhysicalExaminationQuery } from '@higo/common/src/features/ExaminationPage/hooks/queries';
import { defaultNAValue } from '@higo/common/src/utils';

export type BloodPressureMeasurementEntryProps = {
  isAvailable?: boolean;
  examinationId: number;
  isVisitSection?: boolean;
  isCanceledStatus?: boolean;
};

type BloodPressureMeasurementData = {
  systolic?: number | string;
  diastolic?: number | string;
  unit?: string;
};

const BloodPressureContainer = styled.span`
  display: inline-flex;
  gap: 16px;
  align-items: baseline;
  text-transform: none;
`;

const BloodPressureItem = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
`;

const BloodPressureLabel = styled.span`
  font-weight: 400;
`;

const BloodPressureValue = styled.span`
  font-weight: 700;
`;

const formatBloodPressureMeasurement = (
  data?: BloodPressureMeasurementData,
): ReactNode => {
  if (!data) {
    return defaultNAValue;
  }

  if (data.systolic === undefined || data.diastolic === undefined) {
    return defaultNAValue;
  }

  const normalizedUnit = data.unit?.trim();
  const unit =
    normalizedUnit?.toLowerCase() === 'mmhg' ? 'mmHg' : normalizedUnit || 'mmHg';

  return (
    <BloodPressureContainer>
      <BloodPressureItem>
        <BloodPressureLabel>SYS</BloodPressureLabel>
        <BloodPressureValue>{data.systolic}</BloodPressureValue>
        <BloodPressureValue>{unit}</BloodPressureValue>
      </BloodPressureItem>
      <BloodPressureItem>
        <BloodPressureLabel>DIA</BloodPressureLabel>
        <BloodPressureValue>{data.diastolic}</BloodPressureValue>
        <BloodPressureValue>{unit}</BloodPressureValue>
      </BloodPressureItem>
    </BloodPressureContainer>
  );
};

export const BloodPressureMeasurementEntry: FC<BloodPressureMeasurementEntryProps> =
  memo(({ examinationId, isAvailable, isVisitSection, isCanceledStatus }) => {
    const { data, isLoading } = useBloodPressurePhysicalExaminationQuery(
      examinationId,
      isAvailable,
    );

    return (
      <MeasurementEntry
        isVisitSection={isVisitSection}
        title="Blood Pressure"
        measurement={
          isLoading ? (
            <MeasurementSkeleton />
          ) : (
            formatBloodPressureMeasurement(data)
          )
        }
        isCanceledStatus={isCanceledStatus}
      />
    );
  });
